import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User as UserIcon, Share2, BarChart, TrendingUp, Crown, Users, Activity, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';

const Profile = () => {
  const { user, logout } = useAuth();
  const [chartView, setChartView] = useState('bar');
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [hoveredKarder, setHoveredKarder] = useState(null);
  const [hoveredOnlineUser, setHoveredOnlineUser] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [globalStats, setGlobalStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loadingGlobalStats, setLoadingGlobalStats] = useState(true);
  const [loadingUserStats, setLoadingUserStats] = useState(true);

  useEffect(() => {
    fetchData();
    
    // Real-time online users update (every 10 seconds)
    const interval = setInterval(() => {
      fetchOnlineUsers();
    }, 10000);
    
    // Heartbeat to keep user online (update last_active every 30 seconds)
    const heartbeat = setInterval(async () => {
      try {
        await api.post('/user/heartbeat');
      } catch (error) {
        console.error('Heartbeat failed');
      }
    }, 30000);
    
    // Initial heartbeat
    api.post('/user/heartbeat').catch(() => {});
    
    return () => {
      clearInterval(interval);
      clearInterval(heartbeat);
    };
  }, []);

  const fetchOnlineUsers = async () => {
    try {
      const response = await api.get('/user/online-users');
      if (response.data.success) {
        setOnlineUsers(response.data.users);
      }
    } catch (error) {
      console.error('Failed to fetch online users');
    }
  };

  const fetchData = async () => {
    try {
      // Fetch all data in parallel including user profile for fresh stats
      const [profileRes, leaderboardRes, onlineRes, statsRes] = await Promise.all([
        api.get('/user/profile'),
        api.get('/user/leaderboard'),
        api.get('/user/online-users'),
        api.get('/user/stats')
      ]);
      
      // Set user stats from profile
      if (profileRes.data.success) {
        const userData = profileRes.data.user;
        setUserStats({
          totalCards: userData.total_cards_checked || 0,
          approvedCards: userData.approved_cards || 0,
          liveCards: userData.total_live || 0,
          deadCards: userData.total_dead || 0,
          totalHits: (userData.approved_cards || 0) + (userData.total_live || 0),
        });
        setLoadingUserStats(false);
      }
      
      if (leaderboardRes.data.success) setLeaderboard(leaderboardRes.data.leaderboard.slice(0, 5));
      if (onlineRes.data.success) setOnlineUsers(onlineRes.data.users);
      if (statsRes.data.success) {
        setGlobalStats(statsRes.data.stats);
        setLoadingGlobalStats(false);
      }
    } catch (error) {
      console.error('Failed to fetch data');
      setLoadingUserStats(false);
      setLoadingGlobalStats(false);
    }
  };

  const livePercentage = userStats && userStats.totalCards > 0 ? (userStats.liveCards / userStats.totalCards) * 100 : 0;
  const deadPercentage = userStats && userStats.totalCards > 0 ? (userStats.deadCards / userStats.totalCards) * 100 : 0;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'PREMIUM':
      case 'premium':
        return { label: '✦ Premium User', color: 'bg-gradient-to-r from-yellow-500 to-orange-500' };
      case 'ADMIN':
      case 'admin':
        return { label: '⚡ Admin', color: 'bg-gradient-to-r from-blue-500 to-cyan-500' };
      case 'OWNER':
      case 'owner':
        return { label: '👑 Owner', color: 'bg-gradient-to-r from-purple-500 to-pink-500' };
      default:
        return null;
    }
  };

  const getNameStyle = (status) => {
    switch (status) {
      case 'PREMIUM':
      case 'premium':
        return 'bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent';
      case 'ADMIN':
      case 'admin':
        return 'bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent';
      case 'OWNER':
      case 'owner':
        return 'bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent';
      default:
        return 'text-white';
    }
  };

  const getRingColor = (status) => {
    switch (status) {
      case 'PREMIUM':
      case 'premium':
        return 'ring-[3px] ring-yellow-500';
      case 'ADMIN':
      case 'admin':
        return 'ring-[3px] ring-blue-500';
      case 'OWNER':
      case 'owner':
        return 'ring-[3px] ring-purple-500';
      default:
        return 'ring-[3px] ring-gray-500/50';
    }
  };

  const displayedUsers = showAllUsers ? onlineUsers : onlineUsers.slice(0, 3);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1b2a] to-[#0a1628] pb-24">
      <div className="max-w-md mx-auto px-4 py-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center gap-4 mb-6">
            <motion.div className={`w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center ${getRingColor(user?.status)} overflow-hidden`} whileHover={{ scale: 1.05 }}>
              {user?.profile_photo_url ? (
                <img src={user.profile_photo_url} alt={user.username} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>'; }} />
              ) : (
                <UserIcon size={32} className="text-white" />
              )}
            </motion.div>
            <div>
              <h2 className={`text-2xl font-bold ${getNameStyle(user?.status)}`}>
                {user?.first_name && user?.last_name 
                  ? `${user.first_name} ${user.last_name}` 
                  : user?.first_name || user?.username || 'User'}
              </h2>
              <p className="text-gray-400">@{user?.username || 'username'}</p>
            </div>
          </div>

          <motion.div className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50 shadow-lg" whileHover={{ borderColor: 'rgba(6, 182, 212, 0.3)' }} animate={isSpinning ? { rotateY: 360 } : { rotateY: 0 }} transition={{ duration: 0.6 }} style={{ transformStyle: 'preserve-3d' }} onClick={() => { setIsSpinning(true); setTimeout(() => setIsSpinning(false), 600); }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">CyborX Webapp</h3>
              <motion.div className={`w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center ${getRingColor(user?.status)} overflow-hidden`} whileHover={{ scale: 1.1 }}>
                {user?.profile_photo_url ? (
                  <img src={user.profile_photo_url} alt={user.username} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>'; }} />
                ) : (
                  <UserIcon size={20} className="text-white" />
                )}
              </motion.div>
            </div>
            <p className="text-gray-400 text-sm mb-6">
              {user?.first_name && user?.last_name 
                ? `${user.first_name} ${user.last_name}` 
                : user?.first_name || user?.username || 'User'} 
              {' (@'}{user?.username || 'username'}{')'} 
            </p>

            {/* Status, Plan, Expiry */}
            <div className="mb-6 space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Status:</span>
                <span className={`font-semibold ${
                  user?.status === 'OWNER' ? 'text-purple-400' :
                  user?.status === 'ADMIN' ? 'text-cyan-400' :
                  user?.status === 'PREMIUM' ? 'text-yellow-400' :
                  'text-gray-400'
                }`}>{user?.status || 'FREE'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Plan:</span>
                <span className="text-white font-semibold">{user?.plan || 'N/A'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Expiry:</span>
                <span className="text-white font-semibold">
                  {user?.expiry && user.expiry !== 'N/A' ? (
                    <>
                      {user.expiry}
                      {(() => {
                        try {
                          // Parse YYYY-MM-DD format from backend
                          const expiryDate = new Date(user.expiry + 'T23:59:59');
                          const now = new Date();
                          const diffMs = expiryDate - now;
                          
                          if (diffMs <= 0) {
                            return ' (Expired)';
                          }
                          
                          const daysLeft = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                          const hoursLeft = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                          
                          if (daysLeft > 0) {
                            return ` (${daysLeft}d ${hoursLeft}h left)`;
                          } else {
                            return ` (${hoursLeft}h left)`;
                          }
                        } catch {
                          return '';
                        }
                      })()}
                    </>
                  ) : 'N/A'}
                </span>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-2 mb-4">
                <UserIcon size={18} className="text-cyan-400" />
                <h4 className="text-white font-semibold">Your Stats</h4>
              </div>
              {loadingUserStats ? (
                <div className="space-y-3 animate-pulse">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Cards:</span>
                    <div className="h-5 w-16 bg-gray-700 rounded"></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Hits:</span>
                    <div className="h-5 w-12 bg-gray-700 rounded"></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Charged:</span>
                    <div className="h-5 w-12 bg-gray-700 rounded"></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Live:</span>
                    <div className="h-5 w-12 bg-gray-700 rounded"></div>
                  </div>
                </div>
              ) : userStats && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Cards:</span>
                    <span className="text-white font-bold">{userStats.totalCards}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Hits:</span>
                    <span className="text-cyan-400 font-bold">{userStats.totalHits}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Charged:</span>
                    <span className="text-orange-400 font-bold">{userStats.approvedCards}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Live:</span>
                    <span className="text-green-400 font-bold">{userStats.liveCards}</span>
                  </div>
                </div>
              )}
            </div>

            {loadingGlobalStats ? (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp size={18} className="text-cyan-400" />
                  <h4 className="text-white font-semibold">Global Stats</h4>
                </div>
                <div className="space-y-3 animate-pulse">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Checked:</span>
                    <div className="h-5 w-24 bg-gray-700 rounded"></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Hits:</span>
                    <div className="h-5 w-16 bg-gray-700 rounded"></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Charged:</span>
                    <div className="h-5 w-16 bg-gray-700 rounded"></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Live:</span>
                    <div className="h-5 w-16 bg-gray-700 rounded"></div>
                  </div>
                </div>
              </div>
            ) : globalStats && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp size={18} className="text-cyan-400" />
                  <h4 className="text-white font-semibold">Global Stats</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Checked:</span>
                    <span className="text-white font-bold">{globalStats.totalCardsChecked?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Hits:</span>
                    <span className="text-cyan-400 font-bold">{((globalStats.totalApproved || 0) + (globalStats.totalLive || 0)).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Charged:</span>
                    <span className="text-orange-400 font-bold">{globalStats.totalApproved?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Total Live:</span>
                    <span className="text-green-400 font-bold">{globalStats.totalLive?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            <p className="text-gray-500 text-xs text-center mb-4">
              Generated by CyborX Webapp<br />
              t.me/CyborXxRobot
            </p>

            <div className="flex gap-3">
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="flex-1 py-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-xl border border-gray-700/50 text-white font-medium flex items-center justify-center gap-2">
                <Share2 size={18} />
                Share
              </motion.button>
              
              <motion.button 
                onClick={logout}
                whileHover={{ scale: 1.02 }} 
                whileTap={{ scale: 0.98 }} 
                className="flex-1 py-3 bg-red-600/20 hover:bg-red-600/30 rounded-xl border border-red-500/50 text-red-400 hover:text-red-300 font-medium flex items-center justify-center gap-2"
              >
                <LogOut size={18} />
                Logout
              </motion.button>
            </div>
          </motion.div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50 mb-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users size={20} className="text-green-400" />
              <h3 className="text-xl font-bold text-white">Online Users</h3>
            </div>
            <div className="px-3 py-1 bg-green-500/20 border border-green-500/30 rounded-full">
              <span className="text-green-400 font-bold text-sm">{onlineUsers.length}</span>
            </div>
          </div>

          <div className="space-y-3">
            {displayedUsers.map((onlineUser, index) => {
              const statusBadge = getStatusBadge(onlineUser.status);
              const isHovered = hoveredOnlineUser === onlineUser.id;
              return (
                <motion.div key={onlineUser.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + index * 0.05 }} onMouseEnter={() => setHoveredOnlineUser(onlineUser.id)} onMouseLeave={() => setHoveredOnlineUser(null)} className="bg-gray-900/50 rounded-xl p-3 border border-gray-700/50 cursor-pointer hover:bg-gray-800/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-xl ${getRingColor(onlineUser.status || 'FREE')} overflow-hidden`}>
                        {onlineUser.profile_photo_url ? (
                          <img src={onlineUser.profile_photo_url} alt={onlineUser.username} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '👤'; }} />
                        ) : (
                          '👤'
                        )}
                      </div>
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-900"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className={`font-semibold truncate ${getNameStyle(onlineUser.status || 'FREE')}`}>
                          {onlineUser.first_name && onlineUser.last_name 
                            ? `${onlineUser.first_name} ${onlineUser.last_name}`
                            : onlineUser.first_name || onlineUser.username}
                        </p>
                        <AnimatePresence>
                          {statusBadge && isHovered && (
                            <motion.div initial={{ opacity: 0, scale: 0.8, x: 10 }} animate={{ opacity: 1, scale: 1, x: 0 }} exit={{ opacity: 0, scale: 0.8, x: 10 }} className={`px-3 py-1 rounded-full text-white text-xs font-semibold whitespace-nowrap ${statusBadge.color}`}>
                              {statusBadge.label}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      <p className="text-gray-400 text-xs">ID: {onlineUser.id}</p>
                      <p className="text-gray-500 text-xs">@{onlineUser.username}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {onlineUsers.length > 3 && (
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setShowAllUsers(!showAllUsers)} className="w-full mt-4 py-2 text-cyan-400 hover:text-cyan-300 text-sm font-medium">
              {showAllUsers ? 'Show Less' : `Show ${onlineUsers.length - 3} More Users`}
            </motion.button>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-gradient-to-br from-gray-800/40 to-gray-900/40 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50 mb-6 shadow-lg">
          <div className="flex items-center gap-2 mb-4">
            <Crown size={20} className="text-yellow-400" />
            <h3 className="text-xl font-bold text-white">Top Karders</h3>
          </div>

          <div className="space-y-3">
            {leaderboard.map((karder, index) => {
              const statusBadge = getStatusBadge(karder.status);
              const isHovered = hoveredKarder === index;
              return (
                <motion.div key={karder.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + index * 0.05 }} onMouseEnter={() => setHoveredKarder(index)} onMouseLeave={() => setHoveredKarder(null)} className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50 cursor-pointer hover:bg-gray-800/50 transition-colors">
                  <div className="flex items-center gap-3">
                    {index === 0 ? <Crown size={20} className="text-yellow-400" /> : <span className="text-gray-400 font-bold text-lg">#{index + 1}</span>}
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-xl ${getRingColor(karder.status)} overflow-hidden`}>
                      {karder.profile_photo_url ? (
                        <img src={karder.profile_photo_url} alt={karder.username} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '👤'; }} />
                      ) : (
                        '👤'
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`font-bold truncate ${getNameStyle(karder.status)}`} title={karder.first_name && karder.last_name 
                            ? `${karder.first_name} ${karder.last_name}`
                            : karder.first_name || karder.username}>
                          {karder.first_name && karder.last_name 
                            ? `${karder.first_name} ${karder.last_name}`
                            : karder.first_name || karder.username}
                        </p>
                        <AnimatePresence>
                          {statusBadge && isHovered && (
                            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} className={`px-2 py-0.5 rounded-full text-white text-xs font-semibold whitespace-nowrap ${statusBadge.color}`}>
                              {statusBadge.label}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      <p className="text-gray-400 text-xs truncate">ID: {karder.id}</p>
                      <p className="text-gray-500 text-xs truncate">@{karder.username}</p>
                      <p className="text-cyan-400 text-sm font-semibold">{karder.total_hits?.toLocaleString()} total hits</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-700/50">
          <div className="flex items-center gap-2 mb-6">
            <BarChart size={20} className="text-white" />
            <h3 className="text-xl font-bold text-white">Statistics</h3>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
              <p className="text-gray-400 text-xs mb-1">TOTAL CARDS</p>
              <p className="text-white text-2xl font-bold">{userStats?.totalCards || 0}</p>
            </div>
            <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
              <p className="text-gray-400 text-xs mb-1">HIT RATE</p>
              <p className="text-white text-2xl font-bold">
                {userStats && userStats.totalCards > 0 
                  ? ((userStats.totalHits / userStats.totalCards) * 100).toFixed(1) 
                  : '0.0'}%
              </p>
            </div>
            <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
              <p className="text-gray-400 text-xs mb-1">HITS CARD</p>
              <p className="text-white text-2xl font-bold">{userStats?.totalHits || 0}</p>
            </div>
            <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-700/50">
              <p className="text-gray-400 text-xs mb-1">GLOBAL AVG</p>
              <p className="text-white text-2xl font-bold">{globalStats?.globalHitRate || '1.9%'}</p>
            </div>
          </div>

          <div className="flex gap-2 mb-6">
            {[{ id: 'bar', icon: BarChart }, { id: 'donut', icon: Activity }, { id: 'area', icon: TrendingUp }].map((view) => {
              const Icon = view.icon;
              return (
                <motion.button key={view.id} whileHover={{ scale: 1.05 }} onClick={() => setChartView(view.id)} className={`flex-1 py-2 rounded-lg font-medium flex items-center justify-center gap-2 ${chartView === view.id ? 'bg-cyan-500 text-white' : 'bg-gray-800/50 text-gray-400'}`}>
                  <Icon size={16} />
                </motion.button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            {chartView === 'bar' && (
              <motion.div key="bar" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="relative h-64">
                <div className="absolute inset-0 flex items-end justify-around">
                  <div className="flex items-end justify-center gap-8 w-full">
                    <div className="flex flex-col items-center gap-2">
                      <motion.div initial={{ height: 0 }} animate={{ height: `${userStats ? (userStats.totalCards / (userStats.totalCards || 1)) * 200 : 0}px` }} transition={{ delay: 0.3, duration: 0.5 }} className="w-16 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-lg" />
                      <span className="text-gray-400 text-sm">Total</span>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <motion.div initial={{ height: 0 }} animate={{ height: `${userStats ? (userStats.totalHits / (userStats.totalCards || 1)) * 200 : 0}px` }} transition={{ delay: 0.4, duration: 0.5 }} className="w-16 bg-gradient-to-t from-cyan-600 to-cyan-400 rounded-t-lg" />
                      <span className="text-gray-400 text-sm">Hits</span>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <motion.div initial={{ height: 0 }} animate={{ height: `${userStats ? (userStats.approvedCards / (userStats.totalCards || 1)) * 200 : 0}px` }} transition={{ delay: 0.5, duration: 0.5 }} className="w-16 bg-gradient-to-t from-orange-600 to-orange-400 rounded-t-lg" />
                      <span className="text-gray-400 text-sm">Charged</span>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <motion.div initial={{ height: 0 }} animate={{ height: `${userStats ? (userStats.liveCards / (userStats.totalCards || 1)) * 200 : 0}px` }} transition={{ delay: 0.6, duration: 0.5 }} className="w-16 bg-gradient-to-t from-green-600 to-green-400 rounded-t-lg" />
                      <span className="text-gray-400 text-sm">Live</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {chartView === 'donut' && (
              <motion.div key="donut" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }} className="relative h-64 flex items-center justify-center">
                <svg className="w-64 h-64 -rotate-90">
                  <circle cx="128" cy="128" r="90" fill="none" stroke="#10b981" strokeWidth="40" strokeDasharray={`${userStats ? (userStats.liveCards / (userStats.totalCards || 1)) * 565 : 0} ${userStats ? ((userStats.totalCards - userStats.liveCards) / (userStats.totalCards || 1)) * 565 : 565}`} />
                  <circle cx="128" cy="128" r="90" fill="none" stroke="#f97316" strokeWidth="40" strokeDasharray={`${userStats ? (userStats.approvedCards / (userStats.totalCards || 1)) * 565 : 0} ${userStats ? ((userStats.totalCards - userStats.approvedCards) / (userStats.totalCards || 1)) * 565 : 565}`} strokeDashoffset={userStats ? -(userStats.liveCards / (userStats.totalCards || 1)) * 565 : 0} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <p className="text-orange-400 text-2xl font-bold">{userStats?.approvedCards || 0}</p>
                    <p className="text-green-400 text-xl font-bold">{userStats?.liveCards || 0}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {chartView === 'area' && (
              <motion.div key="area" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="relative h-64">
                <svg className="w-full h-full" viewBox="0 0 300 150">
                  <defs>
                    <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="rgba(6, 182, 212, 0.8)" />
                      <stop offset="100%" stopColor="rgba(6, 182, 212, 0.1)" />
                    </linearGradient>
                  </defs>
                  <motion.path
                    d="M10,140 L75,120 L150,100 L225,90 L290,80 L290,150 L10,150 Z"
                    fill="url(#areaGradient)"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                  />
                  <motion.polyline
                    points="10,140 75,120 150,100 225,90 290,80"
                    fill="none"
                    stroke="#06b6d4"
                    strokeWidth="3"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.5, ease: "easeInOut" }}
                  />
                </svg>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default Profile;
